import { IVectorLayerGroupConfig } from "esdm-map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource, WfsBboxAsWktRequestParams } from "esdm-map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "esdm-map/dist/layers/vector/VectorLayerStrategy";
import { KentRecordType, KentRecordSubType } from "./KentRecordTypes";
import { IWmsLayerGroupConfig } from "esdm-map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";
import { FeatureAdd } from "esdm-map/dist/layers/vector/sources/loaders";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/OgcService/hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: KentRecordType,
        recordSubType: KentRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig | IWmsLayerGroupConfig = null;

        switch (recordType) {
            case KentRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;         

            case KentRecordType.AreaBoundary:
                layerConfig = this.AreaBoundary();
                break;

            case KentRecordType.HistoricLandscapeCharacterisation:
                layerConfig = this.HLC(opts);
                break;
        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case KentRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;  

                case KentRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case KentRecordSubType.RegisteredParksAndGardens:
                    layerConfig = this.RegisteredParksAndGardens(opts);
                    break; 

                case KentRecordSubType.HistoricParksAndGardens:
                    layerConfig = this.HistoricParksAndGardens(opts);
                    break; 

                case KentRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea(opts);
                    break;             
            }
        }

        return layerConfig;
    }

    private AreaBoundary = (): IWmsLayerGroupConfig => {
        return {
            displayName: "Kent Boundary",
            ref: "AreaBoundary",
            type: KentRecordType.AreaBoundary,
            url: this.mapFileUrl,
            displayInLayerSwitcher: false,
            displayInLegend: false,
            visibleOnLoad: true,
            queryable: false,
            layers: [
                {
                    layerName: "AreaBoundary",
                    minZoom: 1,
                    maxZoom: 19
                }
            ]
        }
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: KentRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 2000,
                legend: { line: false, polygon: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    // Kent requested to display points only
                    /*{
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },*/
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }                    
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: KentRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: KentRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.RegisteredParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: KentRecordSubType.RegisteredParksAndGardens },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private HistoricParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Historic Parks and Gardens",
                ref: "HistoricParksAndGardens",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.HistoricParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: KentRecordSubType.HistoricParksAndGardens },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    
    private ConservationArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Conservation Areas",
                ref: "ConservationArea",
                type: KentRecordType.Designation,
                subType: KentRecordSubType.ConservationArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "ConservationAreasWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private HLC = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Historic Landscape Characterisation",
                ref: "HLC",
                type: KentRecordType.HistoricLandscapeCharacterisation,
                subType: null,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false, point: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                overrideFeatureAdd: new FeatureAdd(),
                layers: [
                    {
                        layerName: "HLCPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

}

